import {
  buildLocalRecurringCronExpression,
  describeUtcCronExpression,
  parseRecurringCronFormState,
} from './recurringCronSchedule';
import {
  type FireInstant,
  computeFireInstantsFromExpression,
  enumerateEventBridgeFires,
} from './recurringCronValidation';
import { getBrowserTimezone } from './timezoneData';

export interface LegacyCronMigrationAnalysis {
  needsMigration: boolean;
  storedExpression: string;
  migratedExpression: string;
  migratedExplain: string;
  storedFireInstants: FireInstant[];
  migratedFireInstants: FireInstant[];
}

function calendarSelectorKey(expression: string): string {
  const parts = expression.trim().split(/\s+/);
  if (parts.length < 5) return '';
  const [, , dom, month, dow] = parts;
  return `${dom}|${month}|${dow}`;
}

function utcFireSlotKeys(expression: string): string[] {
  const parts = expression.trim().split(/\s+/);
  if (parts.length < 2) return [];
  return enumerateEventBridgeFires(parts[0], parts[1])
    .map((slot) => `${slot.hour}:${String(slot.minute).padStart(2, '0')}`)
    .sort();
}

function fireSlotKeysEqual(a: string, b: string): boolean {
  if (calendarSelectorKey(a) !== calendarSelectorKey(b)) return false;
  const keysA = utcFireSlotKeys(a);
  const keysB = utcFireSlotKeys(b);
  return keysA.length === keysB.length && keysA.every((key, i) => key === keysB[i]);
}

export function legacyCronSchedulesHaveEquivalentFires(a: string, b: string): boolean {
  return fireSlotKeysEqual(a, b);
}

/**
 * True only for legacy midnight-split hour fields that combine comma-separated segments
 * with range/step (e.g. `21-23/9,0-16/9`). Single-segment range+step UTC storage
 * (e.g. `9-19/6`) and enumerate-and-convert comma-hour lists are not legacy.
 */
export function isLegacyStoredCronExpression(expression: string): boolean {
  const parts = expression.trim().split(/\s+/);
  if (parts.length < 2) return false;
  const hourField = parts[1];
  return hourField.includes(',') && hourField.includes('/');
}

/**
 * Detect advanced recurring crons whose stored UTC fire slots differ from the
 * enumerate-and-convert builder (legacy midnight-split / range+step formats).
 * Hour-only step crons without a slash in the hour field (e.g. minute-step patterns)
 * are out of scope — they passthrough on edit without a migration banner.
 */
export function analyzeLegacyCronMigration(
  storedExpression: string,
  scheduleTimeZone: string | undefined,
  viewerTimeZone: string = getBrowserTimezone()
): LegacyCronMigrationAnalysis | null {
  const stored = storedExpression?.trim();
  if (!stored) return null;
  if (!scheduleTimeZone?.trim()) return null;
  if (!isLegacyStoredCronExpression(stored)) return null;

  const tz = scheduleTimeZone.trim();
  const parsed = parseRecurringCronFormState(stored, tz);
  if (!parsed) return null;

  const migratedExpression = buildLocalRecurringCronExpression(
    parsed,
    {
      dayOfWeek: parsed.dayOfWeek,
      dayOfMonth: parsed.dayOfMonth,
      month: parsed.month,
    },
    tz
  );

  // Only prompt when UTC fire slots actually differ — byte-normalization alone is not a migration.
  if (fireSlotKeysEqual(stored, migratedExpression)) return null;

  return {
    needsMigration: true,
    storedExpression: stored,
    migratedExpression,
    migratedExplain: describeUtcCronExpression(migratedExpression),
    storedFireInstants: computeFireInstantsFromExpression(stored, tz).slice(0, 12),
    migratedFireInstants: computeFireInstantsFromExpression(migratedExpression, tz).slice(0, 12),
  };
}

export function formatFireInstantLabel(fire: FireInstant): string {
  const format = (hour: number, minute: number): string => {
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
  };

  return `${format(fire.localHour, fire.localMinute)} local · ${format(fire.utcHour, fire.utcMinute)} UTC`;
}
